import { Link, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../lib/components/custom/Icon";
import { Image } from "../../lib/components/custom/Image";
import { Badge } from "../../lib/components/ui/badge";
import { Button } from "../../lib/components/ui/button";
import { Card } from "../../lib/components/ui/card";
import { Separator } from "../../lib/components/ui/separator";
import { Skeleton } from "../../lib/components/ui/skeleton";
import { useApi } from "../../lib/hooks/api/use-api";
import { useSplashStore } from "../../lib/hooks/use-store";
import type { GetTypes } from "../../lib/types/api-types";
import Layout from "../layout";

export default function Confirmation() {
	const { t } = useTranslation();
	const { auth, orders, payments } = useApi();
	const { data: session, isPending: isLoadingAuth } = auth.useSession();
	const splash = useSplashStore();
	const search = useSearch({ from: "/confirmation" });
	const sessionId = search?.session_id;
	// Cash orders pass order_id directly instead of session_id
	const orderIdFromUrl = (search as { order_id?: string })?.order_id;
	const isCashOrder = !!orderIdFromUrl && !sessionId;

	const [createdOrderId, setCreatedOrderId] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);

	// Fetch Stripe session to get the Order ID (only for Stripe payments)
	const { data: stripeSessionData, isLoading: isLoadingSession } =
		payments.getSession(isCashOrder ? "" : sessionId || "");

	// Fetch order details once we have the order ID from session metadata
	const {
		data: orderDetailsData,
		isLoading: isLoadingOrder,
		refetch: refetchOrder,
	} = orders.getById({ orderId: (createdOrderId || 0).toString() });

	// Types for explicit type assertions when needed
	type OrderDetails = GetTypes["orders"]["getById"];
	type PaymentSession = GetTypes["payments"]["getSession"];

	useEffect(() => {
		// For cash orders, use order_id from URL directly
		if (isCashOrder && orderIdFromUrl) {
			setCreatedOrderId(parseInt(orderIdFromUrl, 10));
			return;
		}

		// For Stripe orders, extract from session metadata
		const session = stripeSessionData as PaymentSession | undefined;
		if (session?.session?.metadata?.orderId) {
			setCreatedOrderId(parseInt(session.session.metadata.orderId, 10));
		}
	}, [stripeSessionData, isCashOrder, orderIdFromUrl]);

	// Poll for order status update if it is still pending
	useEffect(() => {
		let interval: NodeJS.Timeout;
		const order = orderDetailsData as OrderDetails | undefined;

		if (createdOrderId && order?.order?.status === "pending") {
			interval = setInterval(() => {
				refetchOrder();
			}, 2000); // Poll every 2 seconds
		}

		return () => {
			if (interval) clearInterval(interval);
		};
	}, [createdOrderId, orderDetailsData, refetchOrder]);

	useEffect(() => {
		const checkAuth = async () => {
			if (!sessionId && !isCashOrder) {
				if (!isLoadingSession) {
					setError(t("confirmation.error.no_session"));
				}
				return;
			}

			if (isLoadingAuth) {
				return;
			}

			if (!session?.user?.id) {
				setError(t("confirmation.error.signin_required"));
				return;
			}
		};

		checkAuth();
	}, [sessionId, isCashOrder, isLoadingAuth, session, isLoadingSession, t]);

	// Prepare order data from the fetched order details
	const orderData = orderDetailsData?.order;

	// Check if payment failed - use order data which is most reliable
	const paymentFailed = orderData && orderData.paymentStatus === "failed";
	const isPending = orderData && orderData.status === "pending";

	// Show celebration for successful orders
	useEffect(() => {
		if (
			orderData &&
			!paymentFailed &&
			!isPending &&
			createdOrderId &&
			!sessionStorage.getItem(`celebration-shown-${createdOrderId}`)
		) {
			splash.show({
				title: t("confirmation.celebration.title"),
				description: t("confirmation.celebration.description"),
				variant: "celebration",
				duration: 3000,
			});
			sessionStorage.setItem(`celebration-shown-${createdOrderId}`, "true");
		}
	}, [orderData, paymentFailed, isPending, createdOrderId, splash, t]);

	// Loading state
	if (
		isLoadingAuth ||
		isLoadingSession ||
		isLoadingOrder ||
		(sessionId && !orderData && !error)
	) {
		return (
			<Layout>
				<div className="p-4 sm:p-6 lg:p-8 border rounded-large bg-gradient-main min-h-full">
					<div className="max-w-4xl mx-auto">
						<div className="text-center mb-6 sm:mb-8">
							<Skeleton className="h-16 w-16 sm:h-20 sm:w-20 rounded-full mx-auto mb-4" />
							<Skeleton className="h-6 sm:h-8 w-48 sm:w-64 mx-auto mb-2" />
							<Skeleton className="h-4 w-64 sm:w-96 mx-auto" />
						</div>
						<div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
							<div className="lg:col-span-2 space-y-4 sm:space-y-6">
								<Card className="p-4 sm:p-6">
									<Skeleton className="h-6 w-32 mb-4" />
									<Skeleton className="h-20 w-full" />
								</Card>
							</div>
							<Card className="p-4 sm:p-6">
								<Skeleton className="h-6 w-32 mb-4" />
								<Skeleton className="h-40 w-full" />
							</Card>
						</div>
					</div>
				</div>
			</Layout>
		);
	}

	// Error state
	if (error || !orderData) {
		return (
			<Layout>
				<div className="p-4 sm:p-6 lg:p-8 border rounded-large bg-gradient-main min-h-full">
					<div className="max-w-2xl mx-auto text-center">
						<Card className="p-6 sm:p-8 lg:p-12">
							<div className="flex flex-col items-center gap-4">
								<Icon
									name="TriangleAlert"
									className="w-12 h-12 sm:w-16 sm:h-16 text-destructive"
								/>
								<div>
									<h2 className="text-lg sm:text-xl font-semibold mb-2">
										{t("confirmation.error.header")}
									</h2>
									<p className="text-sm sm:text-base text-muted-foreground mb-6">
										{error || t("confirmation.error.not_found")}
									</p>
									<div className="flex flex-col sm:flex-row gap-3 justify-center">
										<Button
											variant="outline"
											asChild
											className="w-full sm:w-auto"
										>
											<Link to="/cart">
												{t("confirmation.actions.view_cart")}
											</Link>
										</Button>
										<Button
											variant="primary"
											asChild
											className="w-full sm:w-auto"
										>
											<Link to="/">
												{t("confirmation.actions.browse_menu")}
											</Link>
										</Button>
									</div>
								</div>
							</div>
						</Card>
					</div>
				</div>
			</Layout>
		);
	}

	// Order confirmation - handle both successful and failed payments
	return (
		<Layout>
			<div className="p-4 sm:p-6 lg:p-8 border rounded-large min-h-full bg-gradient-main">
				<div className="max-w-4xl md:max-w-6xl mx-auto">
					{/* Header - different for successful vs failed payments */}
					<div className="text-center mb-6 sm:mb-8">
						<div className="flex justify-center">
							{isPending ? (
								<div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-muted flex items-center justify-center animate-pulse">
									<Icon
										name="Loader"
										className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground animate-spin"
									/>
								</div>
							) : (
								<div
									className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center ${paymentFailed ? "bg-destructive" : "bg-primary"}`}
								>
									<Icon
										name={paymentFailed ? "X" : "Check"}
										className={`w-8 h-8 sm:w-10 sm:h-10 ${paymentFailed ? "text-destructive-foreground" : "text-primary-foreground"}`}
									/>
								</div>
							)}
						</div>
						<h1 className="text-2xl mt-4 sm:text-3xl font-manrope font-semibold text-foreground mb-2">
							{isPending
								? t("confirmation.status.processing")
								: paymentFailed
									? t("confirmation.status.payment_failed")
									: t("confirmation.status.confirmed")}
						</h1>
						<p className="text-sm sm:text-base text-muted-foreground px-2">
							{isPending
								? t("confirmation.messages.processing")
								: paymentFailed
									? t("confirmation.messages.payment_failed")
									: t("confirmation.messages.confirmed")}
						</p>
					</div>

					{/* Order Details */}
					<div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
						{/* Order Summary */}
						<div className="lg:col-span-2 space-y-4 sm:space-y-6">
							{/* Order Number */}
							<Card className="p-4 sm:p-6">
								<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
									<div>
										<h3 className="text-base sm:text-lg font-semibold mb-1">
											{t("confirmation.order.order_number", {
												id: orderData.id,
											})}
										</h3>
										<p className="text-muted-foreground text-xs sm:text-sm">
											{t("confirmation.order.placed_on", {
												date: new Date().toLocaleDateString(),
											})}
										</p>
									</div>
									<Badge
										variant={
											isPending
												? "outline"
												: paymentFailed
													? "destructive"
													: "secondary"
										}
										className="w-fit"
									>
										{isPending
											? t("confirmation.status.processing_badge")
											: paymentFailed
												? t("confirmation.status.failed_badge")
												: t("confirmation.status.confirmed_badge")}
									</Badge>
								</div>
							</Card>

							{/* Order Items */}
							<Card className="p-4 sm:p-6">
								<h3 className="text-base sm:text-lg font-semibold mb-4">
									{t("confirmation.order.items_header")}
								</h3>
								<div className="space-y-4">
									{orderData.items.map((item) => {
										const itemTotal = parseFloat(item.totalPrice);

										return (
											<div
												key={item.id}
												className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 border rounded-lg"
											>
												<div className="shrink-0 flex items-center gap-3 sm:block">
													{item.menuItem?.imageUrl ? (
														<Image
															src={item.menuItem.imageUrl}
															alt={item.menuItem.name}
															className="w-16 h-16 sm:w-16 sm:h-16 rounded-lg object-cover"
														/>
													) : (
														<div className="w-16 h-16 rounded-lg bg-accent flex items-center justify-center">
															<Icon
																name="Image"
																className="w-6 h-6 text-muted-foreground"
															/>
														</div>
													)}
													<div className="sm:hidden font-semibold text-lg">
														€{itemTotal.toFixed(2)}
													</div>
												</div>
												<div className="flex-1 min-w-0">
													<div className="flex items-start justify-between gap-2 mb-1">
														<h4 className="font-semibold text-sm sm:text-base">
															{item.menuItem?.name || "Item"}
														</h4>
														<div className="hidden sm:block font-semibold text-base">
															€{itemTotal.toFixed(2)}
														</div>
													</div>
													<div className="text-xs sm:text-sm text-muted-foreground">
														{t("confirmation.order.size", {
															size: item.menuSize?.sizeName || "Standard",
														})}
													</div>
													{item.extras && item.extras.length > 0 && (
														<div className="flex flex-wrap gap-1.5 mt-2">
															{item.extras.map((extra, idx: number) => (
																<Badge
																	key={`${extra.id || extra.menuExtraId}-${idx}`}
																	variant="secondary"
																	className="text-xs"
																>
																	{extra.name}
																	{Number(extra.unitPrice) > 0 &&
																		` +€${Number(extra.unitPrice).toFixed(2)}`}
																	{extra.quantity > 1 && ` x${extra.quantity}`}
																</Badge>
															))}
														</div>
													)}
													<div className="flex items-center gap-2 mt-2">
														<Badge variant="secondary" className="text-xs">
															{t("confirmation.order.qty", {
																quantity: item.quantity,
															})}
														</Badge>
													</div>
												</div>
											</div>
										);
									})}
								</div>
							</Card>

							{/* Delivery Info */}
							{orderData?.address && (
								<Card className="p-4 sm:p-6">
									<h3 className="text-base sm:text-lg font-semibold mb-4">
										{t("confirmation.delivery.header")}
									</h3>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
										<div>
											<h4 className="font-medium mb-2 text-sm sm:text-base">
												{t("confirmation.delivery.address")}
											</h4>
											<div className="text-xs sm:text-sm space-y-1">
												<div className="font-medium">
													{orderData.address.label}
												</div>
												<div>{orderData.address.streetAddress}</div>
												<div>
													{orderData.address.city}, {orderData.address.state}{" "}
													{orderData.address.zipCode}
												</div>
											</div>
										</div>
										<div>
											<h4 className="font-medium mb-2 text-sm sm:text-base">
												{t("confirmation.delivery.contact")}
											</h4>
											<div className="text-xs sm:text-sm space-y-1">
												{orderData.address.phone && (
													<div>{orderData.address.phone}</div>
												)}
												{orderData.address.instructions && (
													<div className="mt-3 p-2 bg-accent rounded text-xs">
														<div className="font-medium mb-1">
															{t("confirmation.delivery.instructions")}
														</div>
														{orderData.address.instructions}
													</div>
												)}
											</div>
										</div>
									</div>
								</Card>
							)}
						</div>

						{/* Order Summary & Status */}
						<div className="lg:col-span-1">
							<Card className="p-4 sm:p-6 lg:sticky lg:top-4">
								<h3 className="text-base sm:text-lg font-semibold mb-4">
									{t("confirmation.summary.header")}
								</h3>

								<div className="space-y-3 mb-6">
									<div className="flex justify-between text-sm sm:text-base">
										<span>{t("confirmation.summary.subtotal")}</span>
										<span>
											€
											{Number(orderData.totalPrice) -
												Number(orderData.deliveryFee)}
										</span>
									</div>
									<div className="flex justify-between text-sm">
										<span>{t("confirmation.summary.delivery")}</span>
										<span className="text-primary">
											{Number(orderData.deliveryFee) === 0
												? "NA"
												: `€${Number(orderData.deliveryFee).toFixed(2)}`}
										</span>
									</div>
									<div className="flex justify-between text-sm">
										<span>{t("confirmation.summary.payment")}</span>
										<span
											className={`font-medium ${orderData.paymentMethod === "cash" ? "text-amber-600" : "text-blue-600"}`}
										>
											{orderData.paymentMethod === "cash"
												? t("confirmation.summary.payment_cash")
												: t("confirmation.summary.payment_card")}
										</span>
									</div>
								</div>

								<Separator className="mb-4" />

								<div className="flex justify-between text-base sm:text-lg font-semibold mb-6">
									<span>{t("confirmation.summary.total")}</span>
									<span>€{Number(orderData.totalPrice).toFixed(2)}</span>
								</div>

								{/* Order Status */}
								<div className="space-y-4 mb-6">
									{isPending ? (
										<div className="flex items-center gap-3">
											<div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center shrink-0">
												<Icon
													name="LoaderCircle"
													className="w-4 h-4 text-muted-foreground animate-spin"
												/>
											</div>
											<div className="min-w-0">
												<div className="font-medium text-sm sm:text-base">
													{t("confirmation.tracking.processing_title")}
												</div>
												<div className="text-xs sm:text-sm text-muted-foreground">
													{t("confirmation.tracking.processing_desc")}
												</div>
											</div>
										</div>
									) : paymentFailed ? (
										<div className="flex items-center gap-3">
											<div className="w-8 h-8 bg-destructive rounded-full flex items-center justify-center shrink-0">
												<Icon
													name="X"
													className="w-4 h-4 text-destructive-foreground"
												/>
											</div>
											<div className="min-w-0">
												<div className="font-medium text-sm sm:text-base">
													{t("confirmation.tracking.failed_title")}
												</div>
												<div className="text-xs sm:text-sm text-muted-foreground">
													{t("confirmation.tracking.failed_desc")}
												</div>
											</div>
										</div>
									) : (
										<div className="flex items-center gap-3">
											<div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center shrink-0">
												<Icon
													name="Package"
													className="w-4 h-4 text-primary-foreground"
												/>
											</div>
											<div className="min-w-0">
												<div className="font-medium text-sm sm:text-base">
													{t("confirmation.tracking.confirmed_title")}
												</div>
												<div className="text-xs sm:text-sm text-muted-foreground">
													{t("confirmation.tracking.confirmed_desc")}
												</div>
											</div>
										</div>
									)}
								</div>

								<div className="space-y-3">
									{paymentFailed ? (
										<>
											<Button variant="primary" className="w-full" asChild>
												<Link to="/cart">
													{t("confirmation.actions.retry_payment")}
												</Link>
											</Button>
											<Button variant="outline" className="w-full" asChild>
												<Link to="/orders">
													{t("confirmation.actions.view_orders")}
												</Link>
											</Button>
										</>
									) : (
										<>
											<Button variant="primary" className="w-full" asChild>
												<Link to="/orders">
													{t("confirmation.actions.view_orders")}
												</Link>
											</Button>
											<Button variant="outline" className="w-full" asChild>
												<Link to="/">
													{t("confirmation.actions.browse_menu")}
												</Link>
											</Button>
										</>
									)}
								</div>
							</Card>
						</div>
					</div>

					{/* Footer Actions */}
					<div className="text-center mt-6 sm:mt-8">
						<p className="text-xs sm:text-sm text-muted-foreground mb-4 px-2">
							{paymentFailed
								? t("confirmation.support.payment_help")
								: t("confirmation.support.order_help")}
						</p>
						<div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
							<Button variant="link" asChild className="text-xs sm:text-sm">
								<a href="tel:+4902083772636">
									{t("confirmation.actions.call_restaurant")}
								</a>
							</Button>
							<span className="hidden sm:inline text-muted-foreground">•</span>
							<Button variant="link" asChild className="text-xs sm:text-sm">
								<a href="mailto:americanwaypizza@gmail.com">
									{t("confirmation.actions.email_support")}
								</a>
							</Button>
						</div>
					</div>
				</div>
			</div>
		</Layout>
	);
}
